import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { Map, OrderedMap, Set } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import {
	ConnectorType,
	IConnector,
	IExportConnectorConfig,
	IExportTemplateResponse,
	ISupportedCharset,
} from '../requesters';
import { ExportCsvConfigurationEntity } from './components/export-csv-configuration/export-csv-configuration.entity';
import {
	DbTableNamingStrategy,
	ExportDbConfigurationEntity,
} from './components/export-db-configuration/export-db-configuration.entity';
import { ExportExcelConfigurationEntity } from './components/export-excel-configuration/export-excel-configuration.entity';
import {
	ExportFileConfigurationEntity,
	FileNameGenStrategy,
} from './components/export-file-configuration/export-file-configuration.entity';
import { ExportPartitionConfigurationEntity } from './components/export-partition-configuration/export-partition-configuration.entity';
import { ExportCharsetEntity } from './components/export-template-details/export-charset.entity';
import {
	ConnectorMode,
	ExportConnectorEntity,
} from './components/export-template-details/export-connector.entity';
import {
	ExportFormat,
	ExportJSONConfigEntity,
	ExportNoConfigEntity,
	ExportParquetConfigEntity,
	ExportTemplateConfig,
	ExportTemplateDetailsEntity,
} from './components/export-template-details/export-template-details.entity';
import { ExportTemplateListItemEntity } from './components/export-template-list/export-template-list-item.entity';
import { ExportTemplateListEntity } from './components/export-template-list/export-template-list.entity';
import { ExportTxtMetaConfigurationEntity } from './components/export-txt-meta-configuration/export-txt-meta-configuration.entity';
import {
	ExportWriteModeConfigurationEntity,
	WriteMode,
} from './components/export-write-mode-configuration/export-write-mode-configuration.entity';
import { ExportXmlConfigurationEntity } from './components/export-xml-configuration/export-xml-configuration.entity';

export type ExportTemplateTypeBE =
	| 'csv'
	| 'txt'
	| 'xml'
	| 'parquet'
	| 'json'
	| 'database'
	| 'neo4j'
	| 'excel';

@Injectable()
export class ExportTemplateAdapter {
	private readonly DatabaseType = 'DATABASE';
	private readonly templateType: { [k: string]: ExportFormat } = {
		CSV: ExportFormat.CSV,
		TXT: ExportFormat.TXT_META,
		XML: ExportFormat.XML,
		PARQUET: ExportFormat.Parquet,
		JSON: ExportFormat.JSON,
		EXCEL: ExportFormat.Excel,
		DATABASE: ExportFormat.None,
	};
	private readonly fileNamingStrategy: { [k: string]: FileNameGenStrategy } = {
		UUID: FileNameGenStrategy.UUID,
		FILE_NAME: FileNameGenStrategy.UserDefinedFileNamePattern,
	};
	public constructor(
		private readonly tagAdapter: TagAdapter,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath
	) {}

	public parseTemplateBasicDetails(response: IExportTemplateResponse): {
		id: number;
		type: ExportTemplateTypeBE;
	} {
		if (response.id === undefined || response.config.type === undefined) {
			throw new Error('missing id or type from export template response');
		}
		const type = this.getExportTemplateType(response.config.type.toLowerCase());
		if (!type) {
			throw new Error('could not parse export template type');
		}
		return {
			id: response.id,
			type,
		};
	}

	public generateList(
		res: ReadonlyArray<IExportTemplateResponse>,
		shouldExcludeInactive = false
	): ExportTemplateListEntity {
		const list = ExportTemplateListEntity.build([
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@export.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@export.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@export.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 3,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@export.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'connectorLabel',
				alias: $localize`:i18n=@@export.list.column.connector:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 5,
			},
			{
				field: 'outputType',
				alias: $localize`:i18n=@@export.list.column.outputType:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 6,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@export.list.column.creationDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 8,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@export.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 9,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@export.list.column.updateDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 10,
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@export.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 11,
			},
		]);
		const tuples = res
			.filter((res) => {
				if (shouldExcludeInactive) {
					return res.metadata?.actif;
				}
				return true;
			})
			.reduce((acc, curr) => {
				const entity = this.generateExportTemplateItem(curr);
				acc.push([`${curr.id}`, entity]);
				return acc;
			}, new Array<[string, ExportTemplateListItemEntity]>());
		return list.setElements(OrderedMap(tuples));
	}

	public generateExportTemplateItem(
		response: IExportTemplateResponse
	): ExportTemplateListItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return ExportTemplateListItemEntity.build({
			id: response.id,
			label: response.metadata?.label ?? '',
			code: response.metadata?.code ?? '',
			description: response.metadata?.description ?? '',
			projectId: response.metadata?.project?.id ?? -1,
			metadataId: response.metadata?.id ?? -1,
			iconId: response.metadata?.icon_id ?? -1,
			updateDate: response.metadata?.update_date
				? new Date(response.metadata.update_date)
				: '',
			creationDate: response.metadata?.creation_date
				? new Date(response.metadata.creation_date)
				: '',
			updatedBy: response.metadata?.updated_by ?? '',
			createdBy: response.metadata?.created_by ?? '',
			tags: this.tagAdapter.generateResponseMetadataTag(
				response.metadata?.tags ?? []
			),
			isActive: response.metadata?.actif ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.metadata?.icon_id ?? -1
			}`,
			outputType: response.config?.type
				? this.templateType[response.config?.type]
				: ExportFormat.None,
			connectorLabel: response.config.connector
				? this.getConnectorLabel(
						response.config.connector.metadata?.label ?? '',
						response.config.connector.connector_type ?? ''
				  )
				: $localize`:i18n=@@depot.list.connector.localMode:`,
			connectorType: response.config.connector?.connector_type ?? '',
		});
	}

	public generateExportTemplate(
		res: IExportTemplateResponse
	): ExportTemplateDetailsEntity {
		const { config } = res;
		return ExportTemplateDetailsEntity.build({
			id: res.id,
			outputType: config.type
				? this.templateType[config.type]
				: ExportFormat.None,
			fileNameConfig: this.generateFileConfigOptions(res),
			config: this.generateConfiguration(res),
			connector: this.generateConnector(res.config.connector),
			partition: this.generatePartitioningOpts(res),
			writeMode: this.generateWritingOptions(res),
			compress: config.compress ? (config.compress as boolean) : false,
			keepOriginalLabels: config.keep_original_labels
				? (config.keep_original_labels as boolean)
				: false,
			path: config.path ? (config.path as string) : '',
		});
	}

	public generateConnectorsList(
		res: ReadonlyArray<IConnector>,
		filterOut = ConnectorMode.None
	): Map<number, ExportConnectorEntity> {
		const tuples = res
			.map<[number, ExportConnectorEntity]>((item, idx) => {
				const type = this.getConnectorType(item.connector_type);
				return [
					idx + 1,
					ExportConnectorEntity.build({
						id: item.id,
						label: `${item.metadata.label} (${type.toUpperCase()})`,
						type,
					}),
				];
			})
			.filter(
				([id, e]) => e.type !== ConnectorMode.None && e.type !== filterOut
			);
		tuples.push([0, ExportConnectorEntity.localConnector()]);
		return Map(tuples);
	}

	public generateCharsets(
		response: ReadonlyArray<ISupportedCharset>
	): Set<ExportCharsetEntity> {
		if (!Array.isArray(response)) {
			return Set();
		}
		const tuple = response
			.map<ExportCharsetEntity>((item) =>
				ExportCharsetEntity.build({
					value: item.label,
					label: item.value,
				})
			)
			.sort((e1, e2) => e1.compareTo(e2));
		return Set(tuple);
	}

	private generateConfiguration(
		res: IExportTemplateResponse
	): ExportTemplateConfig {
		const { type } = res.config;
		if (type === 'csv' || type === 'CSV') {
			return this.getExportCsvConfig(res);
		}
		if (type === 'txt' || type === 'TXT') {
			return this.getExportTxtMetaConfig(res);
		}
		if (type === 'xml' || type === 'XML') {
			return this.getExportXmlConfig(res);
		}
		if (type === 'excel' || type === 'EXCEL') {
			return this.getExportExcelConfig(res);
		}
		if (type === 'database' || type === 'DATABASE') {
			return this.getExportDatabaseConfig(res);
		}
		if (type === 'neo4j' || type === 'NEO4J') {
			return this.getExportNeo4jConfig(res);
		}
		if (type === 'parquet' || type === 'PARQUET') {
			return new ExportParquetConfigEntity();
		}
		if (type === 'json' || type === 'JSON') {
			return new ExportJSONConfigEntity();
		}
		return new ExportNoConfigEntity();
	}

	private getExportCsvConfig(
		res: IExportTemplateResponse
	): ExportCsvConfigurationEntity {
		const { config } = res;
		return ExportCsvConfigurationEntity.build({
			withHeaders: config.with_headers
				? (config.with_headers as boolean)
				: false,
			keepLeadingWhiteSpace: config.keep_leading_white_space
				? (config.keep_leading_white_space as boolean)
				: false,
			keepTrailingWhiteSpace: config.keep_trailing_white_space
				? (config.keep_trailing_white_space as boolean)
				: false,
			separator: config.separator ? (config.separator as string) : '',
			endOfLine: config.end_of_line ? (config.end_of_line as string) : '',
			quote: config.quote ? (config.quote as string) : '',
			shouldApplyOnAllFields: config.should_apply_on_all_fields
				? (config.should_apply_on_all_fields as boolean)
				: false,
			shouldApplyEmptyNullFieldPlaceholderValue:
				config.should_apply_invalid_field_placeholder_value
					? (config.should_apply_invalid_field_placeholder_value as boolean)
					: false,
			emptyNullFieldPlaceholderValue: config.empty_null_field_placeholder_value
				? (config.empty_null_field_placeholder_value as string)
				: '',
			escapeCharacter: config.escape_character
				? (config.escape_character as string)
				: '',
			encoding: config.encoding ? (config.encoding as string) : '',
		});
	}

	private getExportTxtMetaConfig(
		res: IExportTemplateResponse
	): ExportTxtMetaConfigurationEntity {
		const { config } = res;
		return ExportTxtMetaConfigurationEntity.build({
			withHeaders: config.with_headers
				? (config.with_headers as boolean)
				: false,
			keepLeadingWhiteSpace: config.keep_leading_white_space
				? (config.keep_leading_white_space as boolean)
				: false,
			keepTrailingWhiteSpace: config.keep_trailing_white_space
				? (config.keep_trailing_white_space as boolean)
				: false,
			separator: config.separator ? (config.separator as string) : '',
			endOfLine: config.end_of_line ? (config.end_of_line as string) : '',
			quote: config.quote ? (config.quote as string) : '',
			shouldApplyOnAllFields: config.should_apply_on_all_fields
				? (config.should_apply_on_all_fields as boolean)
				: false,
			shouldApplyEmptyNullFieldPlaceholderValue:
				config.should_apply_invalid_field_placeholder_value
					? (config.should_apply_invalid_field_placeholder_value as boolean)
					: false,
			emptyNullFieldPlaceholderValue: config.empty_null_field_placeholder_value
				? (config.empty_null_field_placeholder_value as string)
				: '',
			escapeCharacter: config.escape_character
				? (config.escape_character as string)
				: '',
			encoding: config.encoding ? (config.encoding as string) : '',
			metadata: config.metadata ? (config.metadata as string) : '',
			addDefaultMetadata: config.add_default_metadata
				? (config.add_default_metadata as boolean)
				: false,
		});
	}

	private getExportXmlConfig(
		res: IExportTemplateResponse
	): ExportXmlConfigurationEntity {
		const { config } = res;
		return ExportXmlConfigurationEntity.build({
			groupTag: config.group_tag
				? (config.group_tag as string)
				: 'no group tag',
			childTag: config.child_tag
				? (config.child_tag as string)
				: 'no child tag',
		});
	}

	private getExportExcelConfig(
		res: IExportTemplateResponse
	): ExportExcelConfigurationEntity {
		const { config } = res;
		return ExportExcelConfigurationEntity.build({
			cell: config.cell_coordinates
				? (config.cell_coordinates as string)
				: 'no cell',
			withHeaders: config.with_headers
				? (config.with_headers as boolean)
				: false,
		});
	}

	private getExportDatabaseConfig(
		res: IExportTemplateResponse
	): ExportDbConfigurationEntity {
		const { config } = res;
		const strategy = config.naming_strategy
			? this.getTableNamingStrategy(config.naming_strategy as string)
			: DbTableNamingStrategy.None;
		return ExportDbConfigurationEntity.build({
			targetLabel:
				strategy === DbTableNamingStrategy.UserDefinedLabel
					? config.key
						? (config.key as string)
						: 'unspecified table name'
					: '',
			namingStrategy: strategy,
			schema: config.schema ? (config.schema as string) : '',
		});
	}

	private getExportNeo4jConfig(
		res: IExportTemplateResponse
	): ExportDbConfigurationEntity {
		const { config } = res;
		const strategy = config.naming_strategy
			? this.getTableNamingStrategy(config.naming_strategy as string)
			: DbTableNamingStrategy.None;
		return ExportDbConfigurationEntity.build({
			targetLabel:
				strategy === DbTableNamingStrategy.UserDefinedLabel
					? config.key
						? (config.key as string)
						: 'unspecified table name'
					: '',
			namingStrategy: strategy,
			targetKey: config.node_keys ? (config.node_keys as string) : '',
		});
	}

	private generatePartitioningOpts(
		res: IExportTemplateResponse
	): ExportPartitionConfigurationEntity {
		return ExportPartitionConfigurationEntity.build({
			count: res.config.partition_count
				? (res.config.partition_count as number)
				: -1,
		});
	}

	private generateFileConfigOptions(
		res: IExportTemplateResponse
	): ExportFileConfigurationEntity {
		const { config } = res;
		const type = config.type ? (config.type as string) : undefined;
		if (type !== this.DatabaseType) {
			if (config.file_name) {
				return ExportFileConfigurationEntity.build({
					strategy: FileNameGenStrategy.UserDefinedFileNamePattern,
					userDefinedLabel: config.file_name as string,
					shouldApplyCustomExtension: !!config.should_apply_custom_extension,
					customExtension: (config.custom_extension as string) ?? '',
				});
			}
			return ExportFileConfigurationEntity.build({
				strategy: config.naming_strategy
					? this.fileNamingStrategy[config.naming_strategy as string]
					: FileNameGenStrategy.UUID,
				shouldApplyCustomExtension: !!config.should_apply_custom_extension,
				customExtension: (config.custom_extension as string) ?? '',
			});
		}
		return ExportFileConfigurationEntity.build({});
	}

	private generateWritingOptions(
		res: IExportTemplateResponse
	): ExportWriteModeConfigurationEntity {
		const { config } = res;
		const type = config.type ? (config.type as string) : undefined;
		const mode = config.mode ? (config.mode as string) : undefined;
		return type === 'database' ||
			type === 'DATABASE' ||
			type === 'neo4j' ||
			type === 'NEO4J'
			? ExportWriteModeConfigurationEntity.build({
					isEnabled: true,
					mode: this.selectWritingMode(mode),
			  })
			: ExportWriteModeConfigurationEntity.build({
					isEnabled: false,
					mode: WriteMode.None,
			  });
	}

	private getConnectorType(type: ConnectorType | string): ConnectorMode {
		switch (type) {
			case 'SQL':
				return ConnectorMode.SQL;
			case 'NEO4J':
				return ConnectorMode.Neo4j;
			case 'MONGODB':
				return ConnectorMode.NoSQL;
			case 'HDFS':
				return ConnectorMode.HDFS;
			case 'HTTP':
				return ConnectorMode.HTTP;
			case 'S3':
				return ConnectorMode.S3;
			case 'SFTP':
				return ConnectorMode.SFTP;
			default:
				return ConnectorMode.None;
		}
	}

	private getExportTemplateType(
		type: string
	): ExportTemplateTypeBE | undefined {
		if (type === 'csv') {
			return 'csv';
		}
		if (type === 'xml') {
			return 'xml';
		}
		if (type === 'database') {
			return 'database';
		}
		if (type === 'neo4j') {
			return 'neo4j';
		}
		if (type === 'json') {
			return 'json';
		}
		if (type === 'parquet') {
			return 'parquet';
		}
		if (type === 'excel') {
			return 'excel';
		}
		if (type === 'txt') {
			return 'txt';
		}
		return undefined;
	}

	private getTableNamingStrategy(res: string): DbTableNamingStrategy {
		if (res === 'SAME_AS_DATABLOCK') {
			return DbTableNamingStrategy.SameAsDatablock;
		}
		if (res === 'USER_DEFINED_LABEL') {
			return DbTableNamingStrategy.UserDefinedLabel;
		}
		return DbTableNamingStrategy.None;
	}

	private selectWritingMode(mode: string | undefined): WriteMode {
		if (mode === 'OVERWRITE') {
			return WriteMode.Overwrite;
		}
		if (mode === 'APPEND') {
			return WriteMode.Append;
		}
		if (mode === 'OVERWRITE_TRUNCATE') {
			return WriteMode.Truncate;
		}
		return WriteMode.None;
	}

	private generateConnector(
		config: IExportConnectorConfig | undefined | null
	): ExportConnectorEntity {
		if (config === null || config === undefined) {
			return ExportConnectorEntity.localConnector();
		}
		return ExportConnectorEntity.build({
			id: config.id,
			type: this.getConnectorType(config.connector_type),
			label: config.lib,
		});
	}

	private getConnectorLabel(
		connectorLabel: string,
		connectorType: string
	): string {
		let type = '';
		switch (connectorType) {
			case 'SQL':
				type = '(Bdd)';
				break;
			case 'HTTP':
				type = '(HTTP)';
				break;
			case 'S3':
				type = '(S3 / MinIO)';
				break;
			case 'SFTP':
				type = '(SFTP)';
				break;
			case 'HDFS':
				type = '(HDFS)';
				break;
			case 'NEO4J':
				type = '(Bdd)';
				break;
			case 'MONGODB':
				type = '(Bdd)';
				break;
			default:
				type = '';
				break;
		}
		const label =
			connectorType === 'LOCAL'
				? $localize`:i18n=@@depot.list.connector.localMode:`
				: connectorLabel;
		return `${label} ${type}`.trim();
	}
}
